import { CustomsStatusError, CustomsStatusResponse, SortationCenterError } from "@deliverr/sortation-client";
import {
  SoundFx,
  createDangerNotification,
  createSuccessNotification,
  log,
  logError,
  logStart,
} from "@deliverr/ui-facility";
import { useCommonFlow } from "@deliverr/ui-facility/lib-facility/flow/useCommonFlow";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useAsyncFn } from "react-use";
import { useRecoilValue } from "recoil";

import { sortationIdState } from "sortation/base/sortationIdState";
import { useSortationCenterError } from "sortation/base/useSortationCenterError";
import { useSortationClient } from "sortation/base/useSortationClient";
import { useSortationModal } from "sortation/components/SortationModal";
import { visibleSortationModalValue } from "sortation/components/SortationModal/SortationModalState";
import { parseTrackingInput } from "sortation/utils/parseTrackingInput";
import { TOAST_AUTO_CLOSE } from "sortation/utils/toastAutoClose";
import { UtilityScanModal } from "./modals";
import { UtilityTask } from "./utilityTasks";

export const useUtilityScan = () => {
  const { formatMessage } = useIntl();
  const { successResponse, customResponse, resetNotifications, addAutoCloseNotification } = useCommonFlow();
  const { handleSortationCenterError } = useSortationCenterError();
  const sortationId = useRecoilValue(sortationIdState);
  const sortationClient = useSortationClient();
  const { showModal, hideAllModals } = useSortationModal();
  const visibleModal = useRecoilValue(visibleSortationModalValue);

  const [selectedTask, setSelectedTask] = useState<string>("");
  const [barcode, setBarcode] = useState<string>("");

  const errorResponse = useMemo(() => customResponse(SoundFx.ERROR_FAST, "DANGER"), [customResponse]);

  const handleCustomsScan = useCallback(
    async (code) => {
      try {
        const customsStatusResponse = await sortationClient.retrieveCustomsStatus(code);

        if (customsStatusResponse.customsStatus === CustomsStatusResponse.UNKNOWN_CUSTOMS_STATUS) {
          showModal(UtilityScanModal.UNKNOWN_CUSTOMS_STATUS, {});
        } else if (customsStatusResponse.customsStatus === CustomsStatusResponse.CUSTOMS_ON_HOLD) {
          showModal(UtilityScanModal.CUSTOMS_ON_HOLD, {});
        } else if (customsStatusResponse.customsStatus === CustomsStatusResponse.CUSTOMS_CLEARED) {
          successResponse();
          addAutoCloseNotification(
            createSuccessNotification(
              formatMessage(
                {
                  id: "sortation.customsStatusScan.success",
                  defaultMessage: "Package {code} has been cleared by customs",
                },
                { code }
              )
            ),
            TOAST_AUTO_CLOSE
          );
        } else {
          showModal(UtilityScanModal.CUSTOMS_UNKNOWN_ERROR, {});
        }
      } catch (err) {
        if (err.code === CustomsStatusError.TRACKING_CODE_NOT_FOUND) {
          showModal(UtilityScanModal.TRACKING_CODE_NOT_FOUND, {});
        } else {
          showModal(UtilityScanModal.CUSTOMS_UNKNOWN_ERROR, {});
        }
      }
    },
    [addAutoCloseNotification, formatMessage, showModal, successResponse, sortationClient]
  );

  const [handleUtilityScanState, handleUtilityScan] = useAsyncFn(
    async (input: string) => {
      // Don't allow scan if there's a visible modal
      if (visibleModal) {
        return;
      }

      const ctx = logStart({ fn: "useUtilityScan.handleUtilityScan", input, sortationId });

      // Parse barcode to extract tracking code for 2D data matrix scans
      const code = parseTrackingInput(input);

      // Normalize state
      setBarcode(code);
      resetNotifications();
      hideAllModals();

      try {
        if (selectedTask === UtilityTask.CUSTOMS_STATUS) {
          await handleCustomsScan(code);
        } else {
          await sortationClient.processUtilityScan(code, selectedTask, sortationId!);
          log(ctx, "successfully processed utility scan");

          // Success flash, sound, and toast notification
          successResponse();
          addAutoCloseNotification(
            createSuccessNotification(
              formatMessage({
                id: "sortation.utilityscan.success",
                defaultMessage: "Scanned successfully",
              })
            ),
            TOAST_AUTO_CLOSE
          );
        }
      } catch (err) {
        logError(ctx, err);
        if (err.message === SortationCenterError.SORTATION_CENTER_NOT_FOUND) {
          handleSortationCenterError();
          return;
        }

        // Error flash, sound, and toast notification
        errorResponse();
        addAutoCloseNotification(
          createDangerNotification(
            formatMessage({
              id: "sortation.utilityscan.error",
              defaultMessage: "An unknown error occurred. Please try again.",
            })
          ),
          TOAST_AUTO_CLOSE
        );
      } finally {
        // Reset barcode state
        setBarcode("");
      }
    },
    [selectedTask, sortationId, visibleModal]
  );

  return {
    selectedTask,
    barcode,
    isLoading: handleUtilityScanState.loading,
    setSelectedTask,
    setBarcode,
    handleUtilityScan,
  };
};
